import Vue from 'vue'
import MfaAPI from '@/api/mfa'
import MFA_STEP_TYPES, {
  MFA_STEP_ACTIONS,
  MFA_VERIFICATION_PROVIDERS,
} from '@/constants/mfa-constants'
import { HttpRequestError } from '@/utils/error'
import logger from '@/utils/logger'
import clientLogger from '@/plugins/logger/core'
import AUTH_SCREENS from '@/constants/auth-screens'
import { TRACKING_EVENTS } from '@/constants/tracking'

/** Inline MFA errors are under `WEB` in JSON; response camelize makes it `web`. */
function mfaWebErrorPayload(stepErrorData) {
  if (!stepErrorData || typeof stepErrorData !== 'object') return {}
  const nested =
    stepErrorData.web || stepErrorData.WEB || stepErrorData.wEB || null
  if (
    nested &&
    typeof nested === 'object' &&
    (nested.message != null || nested.title != null)
  ) {
    return nested
  }
  if (stepErrorData.message != null || stepErrorData.title != null) {
    return stepErrorData
  }
  return {}
}

/**
 * Apply MFA init-style SUCCESS payload: session + merged auth customer (OTP code, channels, terms).
 * Used when POST …/init or POST …/step/action returns the same `data` shape as init.
 */
function applyMfaInitSuccessSessionAndCustomer({ commit, dispatch }, data) {
  if (!data || typeof data !== 'object') return

  const { mfaSessionId, customerId, stepType, stepTypeData } = data

  commit('SET_MFA_SESSION', {
    mfaSessionId,
    customerId,
    stepType,
    stepTypeData,
  })

  const customer = {}
  if (customerId !== undefined && customerId !== null) {
    customer.customerId = customerId
  }

  // OTP hints: copy only defined fields so we do not wipe code/channels with undefined.
  // Resend/step-action may omit stepType when still on OTP; treat stepTypeData.otp as signal.
  const treatAsOtpStep =
    stepType === MFA_STEP_TYPES.OTP ||
    (stepType == null &&
      stepTypeData &&
      (stepTypeData.otp !== undefined ||
        stepTypeData.channels !== undefined ||
        stepTypeData.termsAccepted !== undefined))

  if (treatAsOtpStep && stepTypeData) {
    if (stepTypeData.otp !== undefined) customer.code = stepTypeData.otp
    if (stepTypeData.channels !== undefined) {
      customer.channels = stepTypeData.channels
    }
    if (stepTypeData.termsAccepted !== undefined) {
      customer.termsAccepted = stepTypeData.termsAccepted
    }
  }

  if (Object.keys(customer).length > 0) {
    dispatch('auth/setCustomer', customer, { root: true })
  }
}

function mergeAuthCustomerFromOtpStepTypeData(dispatch, stepTypeData) {
  if (!stepTypeData || typeof stepTypeData !== 'object') return

  const customer = {}
  if (stepTypeData.otp !== undefined) customer.code = stepTypeData.otp
  if (stepTypeData.channels !== undefined) {
    customer.channels = stepTypeData.channels
  }
  if (stepTypeData.termsAccepted !== undefined) {
    customer.termsAccepted = stepTypeData.termsAccepted
  }
  if (Object.keys(customer).length > 0) {
    dispatch('auth/setCustomer', customer, { root: true })
  }
}

function isMfaStepActionResend(stepTypeData) {
  const action = String(stepTypeData?.action ?? '')
    .trim()
    .toUpperCase()

  return (
    action === MFA_STEP_ACTIONS.RESEND_OTP ||
    action === MFA_STEP_ACTIONS.SEND_OTP
  )
}

const state = {
  mfaSessionId: null,
  customerId: null,
  stepType: null,
  stepTypeData: null,
  initSessionLoading: false,
  processStepLoading: false,
  stepActionLoading: false,
  getStepStatusLoading: false,
  stepTransitionLoading: false,
  error: null,
  inAppChallengeRequired: false,
}

const getters = {
  mfaSessionId(state) {
    return state.mfaSessionId
  },
  mfaCustomerId(state) {
    return state.customerId
  },
  mfaStep(state) {
    return state.stepType
  },
  mfaStepData(state) {
    return state.stepTypeData || {}
  },
  /**
   * Verification provider from mfaStepTransition: 'NAFATH' | 'UAEKYC'.
   * From stepTypeData.provider.
   */
  mfaVerificationProvider(state) {
    return state.stepTypeData?.provider || null
  },
  /**
   * Provider-specific data from mfaStepTransition.
   * NAFATH: { status, transId, random, expireTime }
   * UAEKYC: { journeyToken }
   */
  mfaVerificationProviderData(state) {
    return state.stepTypeData?.providerData || null
  },
  isMfaNafathVerification(state, getters) {
    return getters.mfaVerificationProvider === MFA_VERIFICATION_PROVIDERS.NAFATH
  },
  isMfaUaeKycVerification(state, getters) {
    return getters.mfaVerificationProvider === MFA_VERIFICATION_PROVIDERS.UAEKYC
  },
  isStepTransitionLoading(state) {
    return state.stepTransitionLoading
  },
  /**
   * In-app approval only. Step data when step is IN_APP_APPROVAL (GET step status / process step IN_PROGRESS).
   * Shape per contract: { status: PENDING, timeout_seconds?, in_app_approval_type?, in_app_approval_type_data?: { checkout_id? } }
   */
  mfaInAppApprovalStepData(state) {
    if (state.stepType !== MFA_STEP_TYPES.IN_APP_APPROVAL) return null
    return state.stepTypeData || null
  },
  isInitSessionLoading(state) {
    return state.initSessionLoading
  },
  isProcessStepLoading(state) {
    return state.processStepLoading
  },
  isStepActionLoading(state) {
    return state.stepActionLoading
  },
  isGetStepStatusLoading(state) {
    return state.getStepStatusLoading
  },
  mfaError(state) {
    return state.error
  },
  inAppChallengeRequired(state) {
    return state.inAppChallengeRequired
  },
}

const mutations = {
  SET_MFA_SESSION(state, { mfaSessionId, customerId, stepType, stepTypeData }) {
    if (mfaSessionId !== undefined) state.mfaSessionId = mfaSessionId
    if (customerId !== undefined) state.customerId = customerId
    if (stepType !== undefined) state.stepType = stepType
    if (stepTypeData !== undefined) state.stepTypeData = stepTypeData
  },
  SET_MFA_STEP(state, { stepType, stepTypeData }) {
    if (stepType !== undefined) state.stepType = stepType
    if (stepTypeData !== undefined) state.stepTypeData = stepTypeData
  },
  SET_MFA_LOADING(state, { name, value }) {
    if (name) state[name] = value
  },
  SET_MFA_ERROR(state, error) {
    state.error = error
  },
  CLEAR_MFA_ERROR(state) {
    state.error = null
  },
  CLEAR_MFA_SESSION(state) {
    state.mfaSessionId = null
    state.customerId = null
    state.stepType = null
    state.stepTypeData = null
    state.error = null
  },
  SET_IN_APP_CHALLENGE_REQUIRED: (state, value) => {
    state.inAppChallengeRequired = value
  },
}

const actions = {
  async initMfaSession(
    { commit, dispatch, rootGetters, rootState },
    { phoneNumber, checkoutId }
  ) {
    const hadAutoSubmitPhone = rootState.auth.autoSubmitPhone
    try {
      dispatch(
        'auth/setStatus',
        { name: 'authenticating', status: true },
        { root: true }
      )
      commit('SET_MFA_LOADING', { name: 'initSessionLoading', value: true })
      commit('CLEAR_MFA_ERROR')

      const resolvedCheckoutId = checkoutId || rootGetters['session/checkoutId']

      logger.debug('MFA init session request', {}, { category: 'mfa' })
      clientLogger.debug('MFA init session request')

      const response = await MfaAPI.initSession({
        phoneNumber,
        checkoutId: resolvedCheckoutId,
      })

      const { status, data } = response.data || {}

      // Contract: Init response status is SUCCESS | ERROR. Failure: status ERROR, data: { message, title, type } (INLINE | FULL_SCREEN)
      if (status === 'ERROR') {
        const errorPayload = mfaWebErrorPayload(data)
        // Use the message from API response
        const errorMessage = errorPayload.message || 'MFA init failed'
        const err = new HttpRequestError(
          errorMessage,
          {
            status: response.status ?? 200,
            data: errorPayload,
          },
          'mfa_init'
        )
        commit('SET_MFA_ERROR', err)
        logger.error('MFA init session failed', err, { category: 'mfa' })
        throw err
      }

      if (status === 'SUCCESS' && data) {
        applyMfaInitSuccessSessionAndCustomer({ commit, dispatch }, data)
      }

      logger.debug('MFA init session response', {}, { category: 'mfa' })
      clientLogger.debug('MFA init session response')

      return response
    } catch (err) {
      commit('SET_MFA_ERROR', err)
      logger.error('MFA init session failed', err, { category: 'mfa' })
      throw err
    } finally {
      if (hadAutoSubmitPhone) {
        dispatch('auth/setAutoSubmitPhone', false, { root: true })
      }
      dispatch(
        'auth/setStatus',
        { name: 'authenticating', status: false },
        { root: true }
      )
      commit('SET_MFA_LOADING', { name: 'initSessionLoading', value: false })
    }
  },

  async processMfaStep(
    { commit, dispatch, getters },
    { mfaSessionId, stepType, stepTypeData }
  ) {
    try {
      commit('SET_MFA_LOADING', { name: 'processStepLoading', value: true })
      commit('CLEAR_MFA_ERROR')

      const sessionId = mfaSessionId || getters.mfaSessionId

      logger.debug('MFA process step request', {}, { category: 'mfa' })
      clientLogger.debug('MFA process step request')

      const response = await MfaAPI.processStep({
        mfaSessionId: sessionId,
        stepType,
        stepTypeData,
      })

      const { status, data } = response.data || {}
      const {
        token,
        stepType: nextStepType,
        stepTypeData: nextStepTypeData,
      } = data || {}

      if (status === 'ERROR') {
        const errorPayload = mfaWebErrorPayload(data)
        // Use the message from API response
        const errorMessage = errorPayload.message || 'MFA step failed'
        const err = new HttpRequestError(
          errorMessage,
          {
            status: response.status ?? 200,
            data: errorPayload,
          },
          'mfa_step'
        )
        commit('SET_MFA_ERROR', err)
        throw err
      }

      // TODO: confirm this - if IN_PROGRESS always comes before SUCCESS?
      if (status === 'SUCCESS' && token) {
        await dispatch('auth/finishMfaLogin', { token }, { root: true })
        commit('CLEAR_MFA_SESSION')
        return response
      }

      if (status === 'IN_PROGRESS' && (nextStepType || nextStepTypeData)) {
        commit('SET_MFA_STEP', {
          stepType: nextStepType,
          stepTypeData: nextStepTypeData,
        })
      }

      if (nextStepType === MFA_STEP_TYPES.IN_APP_APPROVAL) {
        commit('SET_IN_APP_CHALLENGE_REQUIRED', true)
        dispatch('auth/changeToScreen', AUTH_SCREENS.in_app_approval, {
          root: true,
        })
      }

      logger.debug('MFA process step response', {}, { category: 'mfa' })
      clientLogger.debug('MFA process step response')

      return response
    } catch (err) {
      commit('SET_MFA_ERROR', err)
      logger.error('MFA step failed', err, { category: 'mfa' })
      throw err
    } finally {
      commit('SET_MFA_LOADING', { name: 'processStepLoading', value: false })
    }
  },

  async mfaStepAction(
    { commit, dispatch, getters },
    { mfaSessionId, stepType, stepTypeData }
  ) {
    try {
      commit('SET_MFA_LOADING', { name: 'stepActionLoading', value: true })
      commit('CLEAR_MFA_ERROR')

      const sessionId = mfaSessionId || getters.mfaSessionId

      logger.debug('MFA step action request', {}, { category: 'mfa' })
      clientLogger.debug('MFA step action request')

      const response = await MfaAPI.stepAction({
        mfaSessionId: sessionId,
        stepType,
        stepTypeData,
      })

      const { status, data } = response.data || {}
      const apiStatus = String(status ?? '')
        .trim()
        .toUpperCase()

      if (apiStatus === 'ERROR') {
        const errorPayload = mfaWebErrorPayload(data)
        // Use the message from API response
        const errorMessage = errorPayload.message || 'MFA step action failed'
        const err = new HttpRequestError(
          errorMessage,
          {
            status: response.status ?? 200,
            data: errorPayload,
          },
          'mfa_step_action'
        )
        commit('SET_MFA_ERROR', err)
        throw err
      }

      // RESEND_OTP | SEND_OTP + init-like body: refresh session + customer. Any other action: previous step-only update.
      if (
        (apiStatus === 'IN_PROGRESS' || apiStatus === 'SUCCESS') &&
        data &&
        typeof data === 'object'
      ) {
        const fullSessionRefresh =
          data.mfaSessionId != null || data.customerId != null

        if (isMfaStepActionResend(stepTypeData) && fullSessionRefresh) {
          applyMfaInitSuccessSessionAndCustomer({ commit, dispatch }, data)
        } else if (
          isMfaStepActionResend(stepTypeData) &&
          !fullSessionRefresh &&
          data.stepTypeData &&
          (data.stepTypeData.otp !== undefined ||
            data.stepTypeData.channels !== undefined ||
            data.stepTypeData.termsAccepted !== undefined)
        ) {
          // Resend returned new OTP / channels without repeating session ids — still refresh UX + test code.
          commit('SET_MFA_STEP', {
            stepType: data.stepType != null ? data.stepType : getters.mfaStep,
            stepTypeData: data.stepTypeData,
          })
          mergeAuthCustomerFromOtpStepTypeData(dispatch, data.stepTypeData)
        } else if (data.stepType != null || data.stepTypeData != null) {
          commit('SET_MFA_STEP', {
            stepType: data.stepType,
            stepTypeData: data.stepTypeData,
          })
        }
      }

      logger.debug('MFA step action response', {}, { category: 'mfa' })
      clientLogger.debug('MFA step action response')

      return response
    } catch (err) {
      commit('SET_MFA_ERROR', err)
      logger.error('MFA step action failed', err, { category: 'mfa' })
      throw err
    } finally {
      commit('SET_MFA_LOADING', { name: 'stepActionLoading', value: false })
    }
  },

  /**
   * Used for face scan only. POST …/mfa/session/step/transition.
   * In future this can be used to transition to other steps like OTP, IVR, PASSCODE, etc.
   * Response: SUCCESS or IN_PROGRESS → data.stepType, data.stepTypeData (e.g. FACE_SCAN + provider).
   * HTTP layer is usually still 200 OK; business failure is JSON body status "ERROR" + data.WEB (see mfaWebErrorPayload).
   */
  async mfaStepTransition(
    { commit, getters },
    { mfaSessionId, transitionStepType }
  ) {
    const MFA_STEP_TRANSITION_USE_MOCK = false // Set to false when API is ready

    try {
      commit('SET_MFA_LOADING', { name: 'stepTransitionLoading', value: true })
      commit('CLEAR_MFA_ERROR')

      const sessionId = mfaSessionId || getters.mfaSessionId

      logger.debug('MFA step transition request', {}, { category: 'mfa' })
      clientLogger.debug('MFA step transition request')

      let response
      if (MFA_STEP_TRANSITION_USE_MOCK) {
        const mockProvider = MFA_VERIFICATION_PROVIDERS.NAFATH
        const stepTypeData =
          mockProvider === MFA_VERIFICATION_PROVIDERS.NAFATH
            ? {
                provider: MFA_VERIFICATION_PROVIDERS.NAFATH,
                providerData: {
                  status: 'PENDING',
                  transId: 'mock-trans-id-' + Date.now(),
                  random: '34',
                  expireTime: new Date(
                    Date.now() + 5 * 60 * 1000
                  ).toISOString(),
                },
              }
            : {
                provider: MFA_VERIFICATION_PROVIDERS.UAEKYC,
                providerData: {
                  journeyToken: 'mock-journey-token-' + Date.now(),
                },
              }
        response = {
          data: {
            status: 'SUCCESS',
            data: {
              stepType: MFA_STEP_TYPES.FACE_SCAN,
              stepTypeData,
            },
          },
        }
        await new Promise((resolve) => setTimeout(resolve, 600))
      } else {
        response = await MfaAPI.stepTransition({
          mfaSessionId: sessionId,
          stepType: transitionStepType,
        })
      }

      const body = response.data || {}
      const { status, data } = body
      const apiStatus = String(status ?? '')
        .trim()
        .toUpperCase()
      const { stepType, stepTypeData } = data || {}
      if (apiStatus === 'ERROR') {
        const errorPayload = mfaWebErrorPayload(data)
        const errorMessage =
          errorPayload.message || 'MFA step transition failed'

        // log Max attempts error
        if (errorPayload?.title?.toLowerCase().includes('maximum')) {
          Vue?.prototype?.$trackingService?.TrackEvent(
            TRACKING_EVENTS.LOGIN_IN_APP_APPROVAL_MAX_ATTEMPTS_EXCEEDED
              .EVENT_NAME,
            {
              mfa_session_id: sessionId,
              error_reason: errorPayload?.title,
            }
          )
        }

        const err = new HttpRequestError(
          errorMessage,
          { status: response.status ?? 200, data: errorPayload },
          'mfa_step_transition'
        )
        commit('SET_MFA_ERROR', err)
        throw err
      }

      // Align with mfaStepAction: backend may return SUCCESS or IN_PROGRESS with stepType / stepTypeData.
      if (
        (apiStatus === 'IN_PROGRESS' || apiStatus === 'SUCCESS') &&
        (stepType || stepTypeData)
      ) {
        commit('SET_MFA_STEP', {
          stepType,
          stepTypeData,
        })
      }

      logger.debug('MFA step transition response', {}, { category: 'mfa' })
      clientLogger.debug('MFA step transition response')

      return response
    } catch (err) {
      commit('SET_MFA_ERROR', err)
      logger.error('MFA step transition failed', err, { category: 'mfa' })
      throw err
    } finally {
      commit('SET_MFA_LOADING', { name: 'stepTransitionLoading', value: false })
    }
  },

  /**
   * Used for in-app approval only. GET /v1/mfa/session/step/{mfa_session_id}.
   * Response: SUCCESS → data.token; IN_PROGRESS → data.stepType, data.stepTypeData (IN_APP_APPROVAL: status, timeout_seconds, in_app_approval_type, in_app_approval_type_data.checkout_id); ERROR → data.message, title, type.
   */
  async getMfaStepStatus({ commit, dispatch, getters }, mfaSessionId) {
    try {
      commit('SET_MFA_LOADING', { name: 'getStepStatusLoading', value: true })
      commit('CLEAR_MFA_ERROR')

      const sessionId = mfaSessionId || getters.mfaSessionId

      logger.debug('MFA get step status request', {}, { category: 'mfa' })
      clientLogger.debug('MFA get step status request')

      const response = await MfaAPI.getStepStatus({ mfaSessionId: sessionId })

      const { status, data } = response.data || {}
      const {
        token,
        stepType: nextStepType,
        stepTypeData: nextStepTypeData,
      } = data || {}

      // Contract: SUCCESS response has only data.token
      if (status === 'SUCCESS' && token) {
        await dispatch('auth/finishMfaLogin', { token }, { root: true })
        commit('CLEAR_MFA_SESSION')
        return response
      }

      if (status === 'ERROR') {
        const errorPayload = mfaWebErrorPayload(data)
        // Use the message from API response
        const errorMessage =
          errorPayload.message || 'MFA get step status failed'
        const err = new HttpRequestError(
          errorMessage,
          {
            status: response.status ?? 200,
            data: errorPayload,
          },
          'mfa_get_step_status'
        )
        commit('SET_MFA_ERROR', err)
        throw err
      }

      // IN_PROGRESS: stepTypeData (IN_APP_APPROVAL) = { status: PENDING, timeout_seconds?, in_app_approval_type?, in_app_approval_type_data?: { checkout_id? } }
      if (status === 'IN_PROGRESS' && (nextStepType || nextStepTypeData)) {
        commit('SET_MFA_STEP', {
          stepType: nextStepType,
          stepTypeData: nextStepTypeData,
        })
      }

      logger.debug('MFA get step status response', {}, { category: 'mfa' })
      clientLogger.debug('MFA get step status response')

      return response
    } catch (err) {
      commit('SET_MFA_ERROR', err)
      logger.error('MFA get step status failed', err, { category: 'mfa' })
      throw err
    } finally {
      commit('SET_MFA_LOADING', { name: 'getStepStatusLoading', value: false })
    }
  },

  async cancelMfaSession({ commit }, { mfaSessionId }) {
    try {
      commit('SET_MFA_LOADING', {
        name: 'cancelMfaSessionLoading',
        value: true,
      })
      commit('CLEAR_MFA_ERROR')

      const response = await MfaAPI.cancelSession({ mfaSessionId })

      const { status, data } = response.data || {}
      if (status === 'ERROR') {
        const errorPayload = mfaWebErrorPayload(data)
        const errorMessage = errorPayload.message || 'MFA cancel session failed'
        const err = new HttpRequestError(
          errorMessage,
          { status: response.status ?? 200, data: errorPayload },
          'mfa_cancel_session'
        )
        commit('SET_MFA_ERROR', err)
        throw err
      }
    } catch (err) {
      commit('SET_MFA_ERROR', err)
      logger.error('MFA cancel session failed', err, { category: 'mfa' })
      throw err
    } finally {
      commit('SET_MFA_LOADING', {
        name: 'cancelMfaSessionLoading',
        value: false,
      })
    }
  },

  clearMfaSession({ commit }) {
    commit('CLEAR_MFA_SESSION')
  },

  clearMfaError({ commit }) {
    commit('CLEAR_MFA_ERROR')
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
